import { Component, OnInit } from '@angular/core';

import { CurrentUser, User } from '../_models';
import { UserService } from '../_services';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Workplace } from '../_models/workplace';
import { HttpClient } from '@angular/common/http';
import { combineLatest } from 'rxjs';
import { WorklogsService } from '../_services/worklogs.service';
import { MessageService } from 'primeng/api';
import { formatDate } from '@angular/common';
import { Position } from '../_models/position';

@Component({
  templateUrl: 'home.component.html',
  styleUrls: ['home.component.scss'],
})
export class HomeComponent implements OnInit {
  currentUser: CurrentUser;
  worklogForm: FormGroup;
  positions: Position[];
  workplaces: Workplace[];
  displayGdprModal = false;
  requestPending = false;
  thisYear = `${new Date().getFullYear()}:`;
  minDate: Date;
  maxDate: Date;

  constructor(
    private userService: UserService,
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private worklogService: WorklogsService,
    private toastsService: MessageService
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.displayGdprModal = !this.currentUser.gdpr && this.currentUser.role !== 'ROLE_ADMIN';
  }

  ngOnInit(): void {
    combineLatest([
      this.userService.getUserAvailablePositions(this.currentUser.id),
      this.http.get<Workplace[]>('/api/workplaces'),
    ]).subscribe(([positions, workplaces]) => {
      this.positions = positions;
      this.workplaces = workplaces;

      this.worklogForm = this.formBuilder.group({
        hours: this.formBuilder.control(null, [Validators.required, Validators.min(0.5)]),
        date: this.formBuilder.control(new Date(), [Validators.required]),
        position: this.formBuilder.control('', Validators.required),
        workplace: this.formBuilder.control('', Validators.required),
      });
    });
    const today = new Date();
    if (today < new Date(2025, 8, 1)) {
      [this.minDate, this.maxDate] = this.getDateRangesV1(today);
    } else {
      [this.minDate, this.maxDate] = this.getDateRangesV2(today);
    }
  }

  getDateRangesV1(today: Date): [Date, Date] {
    let minDate, maxDate;
    minDate = new Date(today);
    minDate.setDate(1);

    maxDate = new Date(today);
    maxDate.setMonth(today.getMonth() + 1, 0);

    return [minDate, maxDate];
  }

  getDateRangesV2(today: Date): [Date, Date] {
    let minDate, maxDate;
    minDate = new Date(today.getFullYear(), today.getMonth(), 1);
    maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    if (minDate < new Date(2025, 8, 1)) {
      minDate = new Date(2025, 8, 1);
    }

    return [minDate, maxDate];
  }

  onSubmit(): void {
    if (!this.worklogForm.valid) {
      return;
    }
    this.requestPending = true;

    const worklog = this.worklogForm.value;
    const date: Date = worklog.date;

    const dateStr = formatDate(date, 'yyyy-MM-dd', 'pl-Pl');

    const data = {
      hours: worklog.hours,
      date: dateStr,
      positionId: worklog.position.id,
      workplaceId: worklog.workplace.id,
      userId: this.currentUser.id,
    };
    this.worklogService
      .add(data)
      .toPromise()
      .then(() => {
        this.toastsService.add({
          severity: 'success',
          summary: $localize`:@@toast-summary-success:Success`,
          detail: $localize`:@@user-worklog-add-success:Worklog added successfully`,
        });
        this.worklogForm.reset();
      })
      .finally(() => {
        this.requestPending = false;
      });
  }

  roundToHalves(event: Event): void {
    let hours = this.worklogForm.controls.hours?.value;
    if (!hours) {
      return;
    }
    this.worklogForm.controls.hours?.setValue(hours);
    const decimal = (hours * 10) % 10;
    if (decimal !== 0) {
      hours = Math.round(hours - 0.5) + 0.5;
      this.worklogForm.controls.hours?.setValue(hours);
    }
  }

  acceptGdpr(): void {
    this.displayGdprModal = false;
    this.currentUser.gdpr = true;
    localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
    this.userService.acceptGdpr().subscribe();
  }
}
